import { Controller } from "@hotwired/stimulus";

// ---------------------------------------------------------------------------
// Shared, page-level tooltip lifecycle (installed once, not once per element)
// ---------------------------------------------------------------------------
const controllers = new Set();
const REFOCUS_GUARD_MS = 300;
let lifecycleInstalled = false;
let refocusGuard = false;
let refocusGuardTimer = null;

// Remove Bootstrap tip nodes that no live trigger element references.
// (Replaces `$(".tooltip").remove()`, which also destroyed tips owned by
// other live instances and left Bootstrap thinking they were still shown.)
function removeOrphanTooltips() {
  for (const tip of document.querySelectorAll(".tooltip")) {
    if (!tip.querySelector(".tooltip-inner")) continue;
    const owned =
      tip.id && document.querySelector(`[aria-describedby~="${CSS.escape(tip.id)}"]`);
    if (!owned) tip.remove();
  }
}

function hideAllTooltips() {
  for (const controller of controllers) controller.forceHide();
  removeOrphanTooltips();
}

// Page is being hidden/blurred: hide everything synchronously and arm the
// guard so the browser's synthetic refocus on return can't re-show a tooltip.
function suspendTooltips() {
  clearTimeout(refocusGuardTimer);
  refocusGuard = true;
  hideAllTooltips();
}

function resumeTooltips() {
  if (document.hidden) return;
  clearTimeout(refocusGuardTimer);
  refocusGuardTimer = setTimeout(() => {
    refocusGuard = false;
  }, REFOCUS_GUARD_MS);
}

// Turbo snapshots the DOM; make sure no open tooltip is captured in it.
function purgeBeforeTurboCache() {
  hideAllTooltips();
  for (const tip of document.querySelectorAll(".tooltip")) {
    if (tip.querySelector(".tooltip-inner")) tip.remove();
  }
  for (const el of document.querySelectorAll("[aria-describedby^='tooltip']")) {
    el.removeAttribute("aria-describedby");
  }
}

function installTooltipLifecycle() {
  if (lifecycleInstalled) return;
  lifecycleInstalled = true;

  document.addEventListener("visibilitychange", () =>
    document.hidden ? suspendTooltips() : resumeTooltips()
  );
  window.addEventListener("pagehide", suspendTooltips);   // navigation + bfcache
  window.addEventListener("pageshow", resumeTooltips);    // bfcache restore
  window.addEventListener("blur", suspendTooltips);       // alt-tab / app switch
  window.addEventListener("focus", resumeTooltips);
  document.addEventListener("turbo:before-cache", purgeBeforeTurboCache);
  document.addEventListener("turbo:load", resumeTooltips);
}

// Connects to data-controller="tooltip"
export default class TooltipController extends Controller {
  static translatedTitles = new Map();

  connect() {
    // Long press owns the shared tooltip on elements using both controllers.
    if (this.element.dataset.controller?.split(/\s+/).includes("long-press")) return;
    const isDesktop = globalThis.AppConfig.isDesktop;
    const showOnMobile = this.element.dataset?.showOnMobile === "true";

    // Don't enable tooltips on mobile unless explicitly enabled
    if (!isDesktop && !showOnMobile) return;

    this.initializeTooltip();
  }

  initializeTooltip(options = {}) {
    const showOnMobile = this.element.dataset?.showOnMobile === "true";

    installTooltipLifecycle();
    // Stable reference so disconnect() can actually remove it.
    document.addEventListener("click", this.closeTooltip);

    removeOrphanTooltips();

    // Protect the generated tooltip before Bootstrap inserts it into the page.
    const template = document.createElement("div");
    template.innerHTML = this.element.dataset.bsTemplate || bootstrap.Tooltip.Default.template;
    for (const node of template.querySelectorAll(".tooltip, .tooltip-inner")) {
      node.classList.add("notranslate");
      node.setAttribute("translate", "no");
    }

    this.tooltip = new bootstrap.Tooltip(this.element, {
      boundary: document.body,
      sanitize: false,
      customClass: [this.element.dataset.bsCustomClass, "notranslate"].filter(Boolean).join(" ").trim().replace(/\s+/g, " "),
      template: template.innerHTML,
      trigger: showOnMobile ? "hover focus click" : "hover focus",
      ...options
    });
    controllers.add(this);

    this.element.addEventListener("show.bs.tooltip", this.prepareTranslatedTitle);
    this.element.addEventListener("show.bs.tooltip", this.revertRefocusShow);
    this.element.addEventListener("inserted.bs.tooltip", this.translateVisibleTooltip);
    document.addEventListener("itoons:language-change", this.languageChanged);
    this.prepareTranslatedTitle();
  }

  disconnect() {
    controllers.delete(this);

    this.element.removeEventListener("show.bs.tooltip", this.prepareTranslatedTitle);
    this.element.removeEventListener("show.bs.tooltip", this.revertRefocusShow);
    this.element.removeEventListener("inserted.bs.tooltip", this.translateVisibleTooltip);
    document.removeEventListener("itoons:language-change", this.languageChanged);
    document.removeEventListener("click", this.closeTooltip);

    // Finish any open tooltip synchronously so Bootstrap's hide callback has
    // already run before we dispose (disposing mid-transition nulls
    // `_activeTrigger` and makes `_isWithActiveTrigger` throw).
    this.forceHide();

    const tooltip = this.tooltip;
    this.tooltip = null;

    if (!tooltip) return;

    const dispose = () => {
      if (globalThis.bootstrap?.Tooltip.getInstance(this.element) === tooltip) {
        tooltip.dispose();
      }
    };

    // Only reachable if a hide transition was already in flight before
    // disconnect; wait for it to finish rather than disposing mid-transition.
    if (this.element.hasAttribute("aria-describedby")) {
      this.element.addEventListener("hidden.bs.tooltip", dispose, { once: true });
      setTimeout(dispose, 1000);
    } else {
      dispose();
    }
  }

  // Animated hide, fine for ordinary user interaction while the page is visible.
  closeTooltip = () => {
    this.tooltip?.hide();
  };

  // Non-animated hide that completes synchronously. Bootstrap's normal hide
  // finishes on `transitionend`/setTimeout, which stall in background tabs.
  forceHide() {
    const tooltip = this.tooltip;
    if (!tooltip) return;

    const config = tooltip._config;
    const previousAnimation = config?.animation;
    if (config) config.animation = false;
    tooltip.tip?.classList.remove("fade");

    try {
      tooltip.hide(); // resets _activeTrigger/_isHovered and removes the tip
    } finally {
      if (config) config.animation = previousAnimation;
    }
  }

  // True when a show is most likely the browser re-firing focus on the
  // previously focused element after a tab/window switch.
  isRefocusShow() {
    return refocusGuard && this.element.contains(document.activeElement);
  }

  // Runs on `show.bs.tooltip`. Let Bootstrap finish show() (so its internal
  // state is consistent), then revert via the public hide() in a microtask,
  // before the browser paints. Works whether Bootstrap shows sync or via timer.
  revertRefocusShow = () => {
    if (!this.isRefocusShow()) return;
    queueMicrotask(() => this.forceHide());
  };

  languageChanged = () => {
    this.closeTooltip();
    this.prepareTranslatedTitle();
  };

  translateVisibleTooltip = async () => {
    if (this.isRefocusShow()) return; // about to be reverted; skip translating
    const language = localStorage.getItem("selected_lang") || "en";
    const tooltip = this.tooltip;
    const tip = tooltip?.tip;
    if (!tip || language === "en" || this.element.dataset.translationTitleLanguage === language) return;

    const inner = tip.querySelector(".tooltip-inner");
    const block = document.getElementById("google-translate-block");
    const translator = block && this.application.getControllerForElementAndIdentifier(block, "google-translate");
    if (!inner || !translator) return;

    // Translate the actual tooltip: the widget can skip offscreen staging text.
    // Keep the tooltip open while masking its source-language text.
    inner.style.setProperty("color", "transparent", "important");
    tip.classList.add("tooltip-translation-loading");
    tip.setAttribute("aria-busy", "true");
    const source = this.element.dataset.translationTitleSource || inner.textContent;
    const translated = translator.waitForTranslationDone({ root: inner, requireMutation: true });
    for (const node of [tip, inner]) {
      node.classList.remove("notranslate");
      node.setAttribute("translate", "yes");
    }
    try {
      const ready = await translated;
      if (ready && this.tooltip === tooltip &&
        language === (localStorage.getItem("selected_lang") || "en")) {
        const title = inner.textContent;
        this.element.dataset.translationTitleSource = source;
        this.element.dataset.translationTitleLanguage = language;
        this.element.dataset.translationTitleText = title;
        this.element.dataset.bsOriginalTitle = title;
        if (Object.hasOwn(this.element.dataset, "bsTitle")) this.element.dataset.bsTitle = title;
        tooltip._config.title = title;
        TooltipController.translatedTitles.set(JSON.stringify([language, source]), title);
      }
    } finally {
      inner.style.removeProperty("color");
      tip.classList.remove("tooltip-translation-loading");
      tip.setAttribute("aria-busy", "false");
      for (const node of [tip, inner]) {
        node.classList.add("notranslate");
        node.setAttribute("translate", "no");
      }
    }
  };

  prepareTranslatedTitle = () => {
    const language = localStorage.getItem("selected_lang") || "en";
    if (!this.tooltip) return;
    const source = this.element.dataset.translationTitleSource ||
      this.element.dataset.bsOriginalTitle || this.element.dataset.bsTitle;
    if (!source) return;
    this.element.dataset.translationTitleSource = source;
    const title = language === "en" ? source :
      (this.element.dataset.translationTitleLanguage === language && this.element.dataset.translationTitleText) ||
      TooltipController.translatedTitles.get(JSON.stringify([language, source]));
    if (!title) {
      this.element.dataset.translationTitleLanguage = "";
      this.element.dataset.bsOriginalTitle = source;
      if (Object.hasOwn(this.element.dataset, "bsTitle")) this.element.dataset.bsTitle = source;
      this.tooltip._config.title = source;
      return;
    }
    this.element.dataset.translationTitleLanguage = language;
    this.element.dataset.translationTitleText = title;
    this.element.dataset.bsOriginalTitle = title;
    if (Object.hasOwn(this.element.dataset, "bsTitle")) this.element.dataset.bsTitle = title;
    this.tooltip._config.title = title;
  };
}
